import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useFeedBack } from "../../../context/FeedBackContext";
import useAxios from "../../../hooks/useAxios";
import useOrders from "../../../hooks/useOrders";
import useOrdersTypes from "../../../hooks/useOrdersTypes";
import useServices from "../../../hooks/useServices";
import useZones from "../../../hooks/useZones";
import useOrderStatuses from "../../../hooks/useOrderStatuses";
import useCategories from "../../../hooks/useCategories";
import { mainPermissions } from "../../../util/MenuLinks";
import profile from "../../../images/profile.png";
import { safeFormatCLP } from "../../../util/format";
import ActionDropdown from "../../../components/ActionDropdown";
import { ProgressBar } from "react-bootstrap";
import { FaList, FaThLarge, FaFilter, FaChevronDown, FaChevronUp } from "react-icons/fa";

const getTypeText = (typeString) => {
    if (!typeString || typeString === '--') return <span className="text-muted">--</span>;
    const lower = String(typeString).toLowerCase();
    let textClass = "text-secondary";
    let customStyle = {};

    if (lower.includes("mensual") || lower.includes("monthly")) {
        textClass = "text-success";
    } else if (lower.includes("extraordinario") || lower.includes("extraordinary")) {
        textClass = "text-primary";
    } else if (lower.includes("manual")) {
        textClass = "text-warning";
        customStyle = { color: '#d97706' };
    }

    return (
        <span
            className={`fw-bold ${textClass} text-truncate d-inline-block`}
            style={{ maxWidth: '140px', ...customStyle }}
            title={typeString}
        >
            {typeString}
        </span>
    );
};

const Ejemplo1 = () => {
    const { permissions, isSuperAdmin } = useAuth();
    const { setCustomAlert, setLoading } = useFeedBack();

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const getInitialFilters = () => {
        return {
            page: parseInt(searchParams.get('page')) || 1,
            serviceIds: searchParams.get('serviceIds') || '',
            zoneId: searchParams.get('zoneId') || '',
            start: searchParams.get('start') || '',
            end: searchParams.get('end') || '',
            orderTypeId: searchParams.get('orderTypeId') || '',
            categoryId: searchParams.get('categoryId') || '',
            id: searchParams.get('id') || '',
            orderStatusCode: searchParams.get('orderStatusCode') || ''
        };
    };

    const initialFilters = getInitialFilters();
    const [filters, setFilters] = useState(initialFilters);
    const hasInitialActiveFilters = Object.entries(initialFilters).some(([key, val]) => key !== 'page' && val !== '' && val !== null && val !== undefined);
    const [showFilters, setShowFilters] = useState(hasInitialActiveFilters);
    const activeFiltersCount = Object.entries(filters).filter(([key, val]) => key !== 'page' && val !== '' && val !== null && val !== undefined).length;
    const [servicesFilters] = useState({ currentUserServices: 'true', perPage: 500 });
    const [selectedValues, setSelectedValues] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    const [{ services }] = useServices({ params: { ...servicesFilters } }, { useCache: false });
    const [{ zones }] = useZones({ params: { ...servicesFilters } }, { useCache: false });
    const [{ categories }] = useCategories({ params: { orderBy: 'path,asc' } }, { useCache: false });
    const [{ ordersTypes }] = useOrdersTypes({ options: { useCache: false } });
    const [{ orderStatuses }] = useOrderStatuses({ options: { useCache: false } });
    const [{ orders, total, numberOfPages, error: ordersError, loading }, getOrders] = useOrders({ params: { ...filters }, options: { useCache: false } });
    const [{ error: deleteError, loading: deleteLoading }, deleteProvider] = useAxios({ method: 'DELETE' }, { manual: true, useCache: false });

    useEffect(() => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value && value !== '') {
                params.set(key, value.toString());
            }
        });
        setSearchParams(params);
    }, [filters, setSearchParams]);

    useEffect(() => {
        getOrders();
    }, [filters]);

    useEffect(() => {
        setLoading?.({ show: deleteLoading, message: 'Eliminando Pedidos' });
    }, [deleteLoading]);

    useEffect(() => {
        if (selectAll) {
            setSelectedValues(orders?.map?.((value) => value?.id) || []);
        } else {
            setSelectedValues([]);
        }
    }, [selectAll]);

    const handleDelete = (value) => {
        deleteProvider({ url: `/orders/${value?.id}` }).then(() => {
            setCustomAlert({
                title: '¡Operación Exitosa!',
                severity: 'success',
                message: 'El pedido ha sido eliminado exitosamente.',
                show: true
            });
            getOrders();
        });
    };

    const handleSelectValue = (selectedValue) => {
        const value = selectedValues?.includes(Number(selectedValue?.id));
        if (value) {
            setSelectedValues(selectedValues?.filter(n => n !== Number(selectedValue?.id)));
        } else {
            setSelectedValues([...selectedValues, Number(selectedValue?.id)]);
        }
    };

    const handleChange = (e) => {
        setFilters(old => ({
            ...old,
            [e.target.name]: e.target.value,
            page: 1
        }));
    };

    const handleClearFilters = () => {
        setFilters({
            page: 1, serviceIds: '', zoneId: '', start: '', end: '',
            orderTypeId: '', categoryId: '', id: '', orderStatusCode: ''
        });
    };

    const handlePageChange = (page) => {
        setFilters(old => ({ ...old, page: page }));
    };

    const handlePerPageChange = (e) => {
        setFilters(old => ({ ...old, [e.target.name]: e.target.value, page: 1 }));
    };

    return (
        <div>
            {/* Selector de Modo de Vista & Filtros */}
            <div className="card p-3 mb-4 shadow-sm border">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold me-2">Vista:</span>
                        <div className="btn-group" role="group">
                            <Link to="/pedidos" className="btn btn-sm btn-outline-primary">
                                Tabla
                            </Link>
                            <Link to="/pedidos-grilla" className="btn btn-sm btn-primary active">
                                Grilla
                            </Link>
                            <Link to="/pedidos-lista" className="btn btn-sm btn-outline-primary">
                                Lista
                            </Link>
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <button
                            className={`btn btn-sm ${showFilters ? 'btn-primary' : 'btn-outline-secondary'} d-flex align-items-center gap-2`}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <FaFilter />
                            <span>Filtros</span>
                            {activeFiltersCount > 0 && (
                                <span className="badge bg-warning text-dark ms-1">{activeFiltersCount}</span>
                            )}
                            {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                        {activeFiltersCount > 0 && (
                            <button className="btn btn-sm btn-outline-danger" onClick={handleClearFilters}>
                                Limpiar Filtros
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Filtros de Búsqueda Desplegables */}
            {showFilters && (
                <div className="card mb-4 p-3 shadow-sm border">
                    <div className="row">
                        <div className="col-12 mb-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0 text-primary">Filtros de Búsqueda</h5>
                                <button className="btn btn-outline-secondary btn-sm" onClick={handleClearFilters}>
                                    Limpiar Filtros
                                </button>
                            </div>
                        </div>

                    <div className="col-md-3 mb-2">
                        <div className="form-group">
                            <label className="text-primary small fw-bold">Número de pedido</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={filters?.id}
                                onChange={handleChange}
                                name="id"
                                placeholder="Número de pedido..."
                            />
                        </div>
                    </div>
                    <div className="col-md-3 mb-2">
                        <div className="form-group">
                            <label className="text-primary small fw-bold">Servicio</label>
                            <select name="serviceIds" className="form-control form-control-sm" value={filters?.serviceIds} onChange={handleChange}>
                                <option value="">Seleccione uno</option>
                                {services?.map((service, i) => (
                                    <option key={i} value={service?.id}>{service?.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="col-md-3 mb-2">
                        <div className="form-group">
                            <label className="text-primary small fw-bold">Zonas</label>
                            <select name="zoneId" className="form-control form-control-sm" value={filters?.zoneId} onChange={handleChange}>
                                <option value="">Seleccione uno</option>
                                {zones?.map((zone, i) => (
                                    <option key={i} value={zone?.id}>{zone?.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="col-md-3 mb-2">
                        <div className="form-group">
                            <label className="text-primary small fw-bold">Categoría</label>
                            <select name="categoryId" className="form-control form-control-sm" value={filters?.categoryId} onChange={handleChange}>
                                <option value="">Todas</option>
                                {categories?.map((cat, i) => (
                                    <option key={i} value={cat?.id}>{cat?.path || cat?.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>
            )}

            {/* Listado de Tarjetas */}
            {loading ? (
                <div className="text-center p-5 card">
                    <h5>Cargando pedidos...</h5>
                </div>
            ) : orders?.length > 0 ? (
                <>
                    <div className="row g-3">
                        {orders.map((order) => {
                            const isSelected = selectedValues.includes(Number(order.id));
                            return (
                                <div className="col-12 col-md-6 col-xl-4" key={order.id}>
                                    <div
                                        className={`card h-100 border-0 shadow-sm position-relative ${isSelected ? 'border border-primary' : ''}`}
                                        style={{
                                            borderRadius: '12px',
                                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)',
                                            border: '1px solid #e2e8f0',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {/* Borde dentado de factura (Superior) */}
                                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '6px', overflow: 'hidden', zIndex: 4 }}>
                                            <svg width="100%" height="6" preserveAspectRatio="none" viewBox="0 0 100 10">
                                                <polygon points="0,0 5,10 10,0 15,10 20,0 25,10 30,0 35,10 40,0 45,10 50,0 55,10 60,0 65,10 70,0 75,10 80,0 85,10 90,0 95,10 100,0 100,0 0,0" fill="#f4f6f9" />
                                            </svg>
                                        </div>

                                        {/* Borde dentado de factura (Inferior) */}
                                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '6px', overflow: 'hidden', zIndex: 4 }}>
                                            <svg width="100%" height="6" preserveAspectRatio="none" viewBox="0 0 100 10">
                                                <polygon points="0,10 5,0 10,10 15,0 20,10 25,0 30,10 35,0 40,10 45,0 50,10 55,0 60,10 65,0 70,10 75,0 80,10 85,0 90,10 95,0 100,10 100,10 0,10" fill="#f4f6f9" />
                                            </svg>
                                        </div>

                                        {/* Muescas circulares laterales de factura / ticket */}
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: '-10px',
                                                bottom: '44px',
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '50%',
                                                background: '#f4f6f9',
                                                borderRight: '1px solid #cbd5e1',
                                                zIndex: 3
                                            }}
                                        />
                                        <div
                                            style={{
                                                position: 'absolute',
                                                right: '-10px',
                                                bottom: '44px',
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '50%',
                                                background: '#f4f6f9',
                                                borderLeft: '1px solid #cbd5e1',
                                                zIndex: 3
                                            }}
                                        />

                                        {/* Cabecera del Ticket / Facturita */}
                                        <div
                                            className="card-header d-flex align-items-center justify-content-between pt-3 pb-2 px-3 border-0"
                                            style={{ borderBottom: '2px dashed #cbd5e1' }}
                                        >
                                            <div className="d-flex align-items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input mt-0"
                                                    checked={isSelected}
                                                    onChange={() => handleSelectValue(order)}
                                                />
                                                <span className="badge bg-primary fs-6 px-2 py-1 shadow-sm" style={{ borderRadius: '6px' }}>
                                                    <i className="fa fa-receipt me-1"></i> #{order.id}
                                                </span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                {getTypeText(order.order_type || order.type)}
                                                <ActionDropdown
                                                    updateOptionString={"Ver detalles"}
                                                    updatePath={'/pedidos/detalles'}
                                                    id={order.id}
                                                    entity="orders"
                                                    onDelete={() => handleDelete(order)}
                                                />
                                            </div>
                                        </div>

                                        {/* Cuerpo del Ticket */}
                                        <div className="card-body px-3 py-3">
                                            {/* Solicitante */}
                                            <div className="d-flex align-items-center mb-3">
                                                <img
                                                    src={order.image_path || order.user_image_path || profile}
                                                    alt={order.user_name}
                                                    className="rounded-circle me-3 border shadow-sm"
                                                    style={{ width: '46px', height: '46px', objectFit: 'cover' }}
                                                    onError={(e) => { e.target.onerror = null; e.target.src = profile; }}
                                                />
                                                <div className="overflow-hidden">
                                                    <div className="fw-bold text-truncate fs-6" title={order.user_name}>
                                                        {order.user_name || '--'}
                                                    </div>
                                                    <small className="text-muted d-block" style={{ fontSize: '0.78rem' }}>
                                                        Fecha solicitud: {order.created_at || '--'}
                                                    </small>
                                                </div>
                                            </div>

                                            {/* Voucher / Ítem de Consumo */}
                                            <div
                                                className="p-3 mb-3 rounded-3 border"
                                                style={{
                                                    borderStyle: 'dashed'
                                                }}
                                            >
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <small className="text-uppercase fw-bold text-muted" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Servicio</small>
                                                    <span className="fw-semibold text-truncate" style={{ maxWidth: '160px', fontSize: '0.85rem' }} title={order.service_name}>
                                                        {order.service_name || '--'}
                                                    </span>
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center border-top pt-2" style={{ borderColor: '#e2e8f0' }}>
                                                    <small className="text-uppercase fw-bold text-muted" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Monto Total</small>
                                                    <span className="fw-bold text-success fs-5">
                                                        {safeFormatCLP(Number(order.total))}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Estatus y Progreso */}
                                            <div>
                                                <div className="d-flex justify-content-between small mb-1">
                                                    <span className="text-capitalize fw-bold" style={{ color: order.status_color || '#333' }}>
                                                        {order.order_status || order.status_name || '--'}
                                                    </span>
                                                    <span className="fw-bold text-muted">{order.progress || 0}%</span>
                                                </div>
                                                <ProgressBar
                                                    now={order.progress || 0}
                                                    variant={order.status_variant_color || 'info'}
                                                    style={{ height: '7px', borderRadius: '4px' }}
                                                />
                                            </div>
                                        </div>

                                        {/* Talón de la Factura */}
                                        <div
                                            className="card-footer py-2 px-3 text-muted small border-0"
                                            style={{
                                                borderTop: '2px dashed #cbd5e1'
                                            }}
                                        >
                                            <div className="row g-1" style={{ fontSize: '0.75rem' }}>
                                                <div className="col-6 text-truncate" title={order.order_boss_name || order.approver}>
                                                    <span className="text-secondary fw-semibold">Aprobador:</span> {order.order_boss_name || order.approver || '--'}
                                                </div>
                                                <div className="col-6 text-truncate" title={order.acquisition_user}>
                                                    <span className="text-secondary fw-semibold">Enc. Adq:</span> {order.acquisition_user || '--'}
                                                </div>
                                                <div className="col-6">
                                                    <span className="text-secondary fw-semibold">F. Aprob:</span> {order.approved_at || '--'}
                                                </div>
                                                <div className="col-6">
                                                    <span className="text-secondary fw-semibold">F. Envío:</span> {order.sent_at || '--'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Paginación Limpia */}
                    <div className="d-sm-flex text-center justify-content-between align-items-center mt-3 p-3 card shadow-sm border">
                        <div className="dataTables_info text-start">
                            <span className="text-muted small">Total de registros: <strong>{total || 0}</strong></span>
                            <div className="d-flex align-items-center gap-2 mt-1">
                                <small className="text-muted">Mostrar de:</small>
                                <select
                                    name="perPage"
                                    className="form-select form-select-sm border-secondary-subtle"
                                    style={{ width: '80px', cursor: 'pointer' }}
                                    onChange={handlePerPageChange}
                                    value={filters?.perPage || 10}
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 mt-2 mt-sm-0" style={{ maxWidth: '70%' }}>
                            <button
                                className="btn btn-sm btn-light border-0 text-primary fw-bold px-3 me-1"
                                disabled={filters?.page <= 1}
                                onClick={() => filters?.page > 1 && handlePageChange(filters?.page - 1)}
                            >
                                &laquo; Anterior
                            </button>
                            <span className="p-1 d-flex align-items-center custom-horizontal-scrollbar scrollbar-primary" style={{ maxWidth: '54%', overflowX: 'auto' }}>
                                {Array.from(Array(numberOfPages || 1).keys()).map((number, i) => (
                                    <div
                                        key={i}
                                        className={`d-flex align-items-center justify-content-center fw-bold ${filters?.page === (i + 1) ? "bg-primary text-white shadow-sm" : "bg-light text-dark"}`}
                                        onClick={() => handlePageChange(1 + number)}
                                        style={{
                                            minWidth: '34px',
                                            height: '34px',
                                            borderRadius: '8px',
                                            marginRight: '6px',
                                            cursor: 'pointer',
                                            flexShrink: 0,
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        {1 + number}
                                    </div>
                                ))}
                            </span>
                            <button
                                className="btn btn-sm btn-light border-0 text-primary fw-bold px-3 ms-1"
                                disabled={filters?.page >= numberOfPages}
                                onClick={() => filters?.page < numberOfPages && handlePageChange(filters?.page + 1)}
                            >
                                Siguiente &raquo;
                            </button>
                        </div>
                    </div>
                </>
            ) : (
                <div className="card p-5 text-center">
                    <h5>No se encontraron pedidos.</h5>
                </div>
            )}
        </div>
    );
};

export default Ejemplo1;
